/* =========================================================
   INKSOFT SHARED MODAL
   Reusable across demo + product page
   Backward-compatible with existing modal.open(html)

   Quick Preview supports:
   - Product detail loading through window.InksoftPOC.api.getProduct()
   - Color/style switching
   - Product side thumbnails
   - Size refresh per selected style when available
========================================================= */

window.InksoftPOC = window.InksoftPOC || {};

window.InksoftPOC.modal = (function () {
  let modalEl = null;
  let closeBound = false;

  const core = window.InksoftPOC.core || {};
  const api = window.InksoftPOC.api || {};
  const utils = window.InksoftPOC.productUtils || {};

  const quickState = {
    product: null,
    selectedStyleIndex: 0,
    selectedSideIndex: 0
  };

  function escapeHtml(value) {
    if (core && typeof core.escapeHtml === 'function') {
      return core.escapeHtml(value);
    }

    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function asArray(value) {
    if (utils && typeof utils.asArray === 'function') {
      return utils.asArray(value);
    }

    return Array.isArray(value) ? value : [];
  }

  function asString(value) {
    if (utils && typeof utils.asString === 'function') {
      return utils.asString(value);
    }

    return value == null ? '' : String(value).trim();
  }

  function normalizeInksoftUrl(url) {
    if (utils && typeof utils.normalizeInksoftUrl === 'function') {
      return utils.normalizeInksoftUrl(url);
    }

    return asString(url);
  }

  function init() {
    modalEl = document.getElementById('inksoft-modal');
    return modalEl;
  }

  function getModalEl() {
    return modalEl || init();
  }

  function open(html) {
    const el = getModalEl();
    if (!el) return;

    el.innerHTML = html || '';
    el.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    const el = getModalEl();
    if (!el) return;

    el.classList.remove('open');
    el.innerHTML = '';
    document.body.style.overflow = '';

    quickState.product = null;
    quickState.selectedStyleIndex = 0;
    quickState.selectedSideIndex = 0;
  }

  function bindClose() {
    if (closeBound) return;
    closeBound = true;

    document.addEventListener('click', function (e) {
      if (
        e.target.matches('.inksoft-modal-close') ||
        e.target.matches('[data-inksoft-modal-close]') ||
        e.target.matches('.inksoft-modal-overlay')
      ) {
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        close();
      }
    });
  }

  function loading() {
    return `
      <div class="inksoft-modal-overlay"></div>
      <div class="inksoft-modal-content">
        <button class="inksoft-modal-close inksoft-btn" type="button">Close</button>
        <div class="inksoft-loader">Loading...</div>
      </div>
    `;
  }

  function getProductName(product) {
    return asString(
      product && (
        product.name ||
        product.Name ||
        product.title ||
        product.Title ||
        product.productName ||
        product.ProductName
      )
    ) || 'Product';
  }

  function getProductId(product) {
    return asString(
      product && (
        product.id ||
        product.ID ||
        product.Id ||
        product.productId ||
        product.ProductId
      )
    );
  }

  function getProductSku(product) {
    return asString(
      product && (
        product.sku ||
        product.Sku ||
        product.SKU ||
        product.manufacturerSku ||
        product.ManufacturerSku
      )
    );
  }

  function getProductBrand(product) {
    return asString(
      product && (
        product.brand ||
        product.Brand ||
        product.brandName ||
        product.BrandName ||
        product.manufacturerName ||
        product.ManufacturerName
      )
    );
  }

  function getProductSupplier(product) {
    return asString(
      product && (
        product.supplier ||
        product.Supplier ||
        product.supplierName ||
        product.SupplierName
      )
    );
  }

  function getStyles(product) {
    return asArray(
      product && (
        product.styles ||
        product.Styles ||
        product.productStyles ||
        product.ProductStyles ||
        product.styleOptions ||
        product.StyleOptions
      )
    );
  }

  function getStyleName(style) {
    return asString(
      style && (
        style.name ||
        style.Name ||
        style.colorName ||
        style.ColorName ||
        style.displayName ||
        style.DisplayName
      )
    ) || 'Color';
  }

  function getStyleId(style) {
    return asString(
      style && (
        style.id ||
        style.ID ||
        style.Id ||
        style.styleId ||
        style.StyleId ||
        style.productStyleId ||
        style.ProductStyleId
      )
    );
  }

  function getProductSides(product) {
    return asArray(
      product && (
        product.sides ||
        product.Sides ||
        product.images ||
        product.Images
      )
    );
  }

  function getStyleSides(style) {
    if (!style) return [];

    const directSides = asArray(
      style.sides ||
      style.Sides ||
      style.images ||
      style.Images ||
      style.productImages ||
      style.ProductImages
    );

    if (directSides.length) return directSides;

    const generatedSides = [];

    const front = normalizeInksoftUrl(asString(
      style.imageFilePath_Front ||
      style.ImageFilePath_Front ||
      style.frontImageUrl ||
      style.FrontImageUrl ||
      style.imageFilePathFront ||
      style.ImageFilePathFront
    ));

    const back = normalizeInksoftUrl(asString(
      style.imageFilePath_Back ||
      style.ImageFilePath_Back ||
      style.backImageUrl ||
      style.BackImageUrl ||
      style.imageFilePathBack ||
      style.ImageFilePathBack
    ));

    const sideLeft = normalizeInksoftUrl(asString(
      style.imageFilePath_SideLeft ||
      style.ImageFilePath_SideLeft ||
      style.leftImageUrl ||
      style.LeftImageUrl ||
      style.sleeveLeftImageUrl ||
      style.SleeveLeftImageUrl
    ));

    const sideRight = normalizeInksoftUrl(asString(
      style.imageFilePath_SideRight ||
      style.ImageFilePath_SideRight ||
      style.rightImageUrl ||
      style.RightImageUrl ||
      style.sleeveRightImageUrl ||
      style.SleeveRightImageUrl
    ));

    if (front) generatedSides.push({ name: 'Front', imageUrl: front, thumbnailUrl: front });
    if (back) generatedSides.push({ name: 'Back', imageUrl: back, thumbnailUrl: back });
    if (sideLeft) generatedSides.push({ name: 'Left Sleeve', imageUrl: sideLeft, thumbnailUrl: sideLeft });
    if (sideRight) generatedSides.push({ name: 'Right Sleeve', imageUrl: sideRight, thumbnailUrl: sideRight });

    return generatedSides;
  }

  function getCurrentStyle(product) {
    const styles = getStyles(product);

    if (!styles.length) return null;

    const safeIndex = Math.max(
      0,
      Math.min(Number(quickState.selectedStyleIndex) || 0, styles.length - 1)
    );

    quickState.selectedStyleIndex = safeIndex;

    return styles[safeIndex];
  }

  function getCurrentSides(product) {
    const currentStyle = getCurrentStyle(product);
    const styleSides = getStyleSides(currentStyle);

    if (styleSides.length) return styleSides;

    return getProductSides(product);
  }

  function getSideName(side, index) {
    return asString(
      side && (
        side.name ||
        side.Name ||
        side.displayName ||
        side.DisplayName ||
        side.side ||
        side.Side
      )
    ) || (index === 0 ? 'Front' : index === 1 ? 'Back' : 'Side ' + (index + 1));
  }

  function getSideImage(side) {
    if (!side) return '';

    return normalizeInksoftUrl(asString(
      side.imageUrl ||
      side.ImageUrl ||
      side.thumbnailUrl ||
      side.ThumbnailUrl ||
      side.imageFilePath ||
      side.ImageFilePath ||
      side.url ||
      side.Url ||
      side.path ||
      side.Path
    ));
  }

  function pickImageFromStyle(style) {
    if (!style) return '';

    const sides = getStyleSides(style);
    for (let i = 0; i < sides.length; i += 1) {
      const img = getSideImage(sides[i]);
      if (img) return img;
    }

    return normalizeInksoftUrl(asString(
      style.thumbnailUrl ||
      style.ThumbnailUrl ||
      style.imageUrl ||
      style.ImageUrl ||
      style.imageFilePath ||
      style.ImageFilePath ||
      style.swatchImage ||
      style.SwatchImage
    ));
  }

  function pickProductImage(product) {
    const sides = getCurrentSides(product);
    const safeSideIndex = Math.max(
      0,
      Math.min(Number(quickState.selectedSideIndex) || 0, Math.max(0, sides.length - 1))
    );

    quickState.selectedSideIndex = safeSideIndex;

    if (sides.length) {
      const sideImg = getSideImage(sides[safeSideIndex]);
      if (sideImg) return sideImg;
    }

    const styleImg = pickImageFromStyle(getCurrentStyle(product));
    if (styleImg) return styleImg;

    return normalizeInksoftUrl(asString(
      product && (
        product.thumbnailUrl ||
        product.ThumbnailUrl ||
        product.imageUrl ||
        product.ImageUrl ||
        product.primaryImage ||
        product.PrimaryImage ||
        product.image ||
        product.Image
      )
    ));
  }

  function getStyleSwatch(style) {
    if (!style) return '';

    return normalizeInksoftUrl(asString(
      style.swatchImage ||
      style.SwatchImage ||
      style.swatchUrl ||
      style.SwatchUrl ||
      style.thumbnailUrl ||
      style.ThumbnailUrl ||
      style.imageUrl ||
      style.ImageUrl ||
      style.imageFilePath ||
      style.ImageFilePath ||
      style.imageFilePath_Front ||
      style.ImageFilePath_Front
    ));
  }

  function getStyleColor(style) {
    const htmlColor1 = asString(style && (style.htmlColor1 || style.HtmlColor1 || style.HTMLColor1));
    const htmlColor2 = asString(style && (style.htmlColor2 || style.HtmlColor2 || style.HTMLColor2));

    if (htmlColor1) return htmlColor1.charAt(0) === '#' ? htmlColor1 : '#' + htmlColor1;
    if (htmlColor2) return htmlColor2.charAt(0) === '#' ? htmlColor2 : '#' + htmlColor2;

    return '';
  }

  function getStyleSizes(style) {
    return asArray(
      style && (
        style.sizes ||
        style.Sizes ||
        style.availableSizes ||
        style.AvailableSizes
      )
    );
  }

  function getProductSizes(product) {
    return asArray(
      product && (
        product.sizes ||
        product.Sizes ||
        product.availableSizes ||
        product.AvailableSizes
      )
    );
  }

  function normalizeSizeName(size) {
    return asString(
      typeof size === 'string'
        ? size
        : (
            size.name ||
            size.Name ||
            size.code ||
            size.Code ||
            size.size ||
            size.Size
          )
    );
  }

  function buildSwatches(product) {
    const styles = getStyles(product).slice(0, 48);

    if (!styles.length) return '';

    return `
      <div class="inksoft-quick-preview-panel">
        <div class="inksoft-quick-preview-panel__title">Color Options</div>
        <div class="inksoft-quick-preview-swatches">
          ${styles.map(function (style, index) {
            const image = getStyleSwatch(style);
            const color = getStyleColor(style);
            const label = getStyleName(style);
            const isActive = index === quickState.selectedStyleIndex;

            return `
              <button
                type="button"
                class="inksoft-quick-preview-swatch${isActive ? ' is-active' : ''}"
                title="${escapeHtml(label)}"
                data-quick-preview-style-index="${escapeHtml(String(index))}"
              >
                ${
                  image
                    ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(label)}" loading="lazy" decoding="async">`
                    : `<span class="inksoft-quick-preview-swatch__fallback" style="${color ? 'background:' + escapeHtml(color) + ';' : ''}"></span>`
                }
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  function buildSides(product) {
    const sides = getCurrentSides(product);

    if (!sides.length) return '';

    return `
      <div class="inksoft-quick-preview-thumbs" aria-label="Product sides">
        ${sides.map(function (side, index) {
          const image = getSideImage(side);
          const label = getSideName(side, index);
          const isActive = index === quickState.selectedSideIndex;

          return `
            <button
              type="button"
              class="inksoft-quick-preview-thumb${isActive ? ' is-active' : ''}"
              data-quick-preview-side-index="${escapeHtml(String(index))}"
              title="${escapeHtml(label)}"
              aria-label="${escapeHtml(label)}"
            >
              ${
                image
                  ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(label)}" loading="lazy" decoding="async">`
                  : `<span>${escapeHtml(label)}</span>`
              }
            </button>
          `;
        }).join('')}
      </div>
    `;
  }

  function buildSizes(product) {
    const currentStyle = getCurrentStyle(product);
    let sizes = getStyleSizes(currentStyle);

    if (!sizes.length) {
      sizes = getProductSizes(product);
    }

    if (!sizes.length) {
      getStyles(product).forEach(function (style) {
        sizes = sizes.concat(getStyleSizes(style));
      });
    }

    const names = [];
    const seen = {};

    sizes.forEach(function (size) {
      const name = normalizeSizeName(size);

      if (name && !seen[name]) {
        seen[name] = true;
        names.push(name);
      }
    });

    if (!names.length) return '';

    return `
      <div class="inksoft-quick-preview-panel">
        <div class="inksoft-quick-preview-panel__title">Available Sizes</div>
        <div class="inksoft-quick-preview-sizes">
          ${names.slice(0, 24).map(function (name) {
            return `<span class="inksoft-quick-preview-size">${escapeHtml(name)}</span>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  function getDescription(product) {
    return asString(
      product && (
        product.description ||
        product.Description ||
        product.productDescription ||
        product.ProductDescription ||
        product.shortDescription ||
        product.ShortDescription
      )
    );
  }

  function getProductUrl(productId) {
    const id = asString(productId);
    return id ? `/pages/inksoft-product?id=${encodeURIComponent(id)}` : '#';
  }

  function bindQuickPreviewControls() {
    const el = getModalEl();
    if (!el) return;

    Array.prototype.forEach.call(
      el.querySelectorAll('[data-quick-preview-style-index]'),
      function (button) {
        button.addEventListener('click', function () {
          const nextIndex = Number(button.getAttribute('data-quick-preview-style-index'));

          if (!Number.isFinite(nextIndex)) return;

          quickState.selectedStyleIndex = nextIndex;
          quickState.selectedSideIndex = 0;

          if (quickState.product) {
            open(renderQuickPreview(quickState.product));
            bindQuickPreviewControls();
          }
        });
      }
    );

    Array.prototype.forEach.call(
      el.querySelectorAll('[data-quick-preview-side-index]'),
      function (button) {
        button.addEventListener('click', function () {
          const nextIndex = Number(button.getAttribute('data-quick-preview-side-index'));

          if (!Number.isFinite(nextIndex)) return;

          quickState.selectedSideIndex = nextIndex;

          if (quickState.product) {
            open(renderQuickPreview(quickState.product));
            bindQuickPreviewControls();
          }
        });
      }
    );
  }

  function renderQuickPreview(product) {
    const productId = getProductId(product);
    const title = getProductName(product);
    const sku = getProductSku(product);
    const brand = getProductBrand(product);
    const supplier = getProductSupplier(product);
    const image = pickProductImage(product);
    const description = getDescription(product);
    const currentStyle = getCurrentStyle(product);
    const currentStyleName = currentStyle ? getStyleName(currentStyle) : '';

    return `
      <div class="inksoft-modal-overlay"></div>

      <div class="inksoft-quick-preview-modal">
        <div class="inksoft-quick-preview-header">
          <h2 class="inksoft-quick-preview-title">${escapeHtml(title)}</h2>
          <button class="inksoft-modal-close" type="button">Close</button>
        </div>

        <div class="inksoft-quick-preview-body">
          <div class="inksoft-quick-preview-grid">
            <div>
              <div class="inksoft-quick-preview-image-wrap">
                ${
                  image
                    ? `<img class="inksoft-quick-preview-image" src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy" decoding="async">`
                    : `<div class="inksoft-quick-preview-empty">No image available</div>`
                }
              </div>

              ${buildSides(product)}
            </div>

            <div>
              <div class="inksoft-quick-preview-meta">
                ${sku ? `<div><strong>SKU:</strong> ${escapeHtml(sku)}</div>` : ''}
                ${brand ? `<div><strong>Brand:</strong> ${escapeHtml(brand)}</div>` : ''}
                ${supplier ? `<div><strong>Supplier:</strong> ${escapeHtml(supplier)}</div>` : ''}
                ${currentStyleName ? `<div><strong>Selected color:</strong> ${escapeHtml(currentStyleName)}</div>` : ''}
              </div>

              ${buildSwatches(product)}
              ${buildSizes(product)}

              ${
                description
                  ? `<div class="inksoft-quick-preview-description">${description}</div>`
                  : ''
              }

              <div class="inksoft-quick-preview-actions">
                <a class="inksoft-quick-preview-button" href="${escapeHtml(getProductUrl(productId))}">View Product</a>
                <button class="inksoft-modal-close" type="button">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async function openProductQuickView(productId) {
    const id = asString(productId);

    if (!id) {
      open(`
        <div class="inksoft-modal-overlay"></div>
        <div class="inksoft-modal-content">
          <button class="inksoft-modal-close inksoft-btn" type="button">Close</button>
          <div class="inksoft-loader">Missing product ID.</div>
        </div>
      `);
      return;
    }

    open(loading());

    try {
      if (!api || typeof api.getProduct !== 'function') {
        throw new Error('Inksoft API module is not available.');
      }

      const payload = await api.getProduct(id);
      const product = payload && payload.data ? payload.data : payload;

      quickState.product = product || {};
      quickState.selectedStyleIndex = 0;
      quickState.selectedSideIndex = 0;

      open(renderQuickPreview(quickState.product));
      bindQuickPreviewControls();
    } catch (err) {
      console.error('[Inksoft Quick Preview] Failed to load product:', err);

      open(`
        <div class="inksoft-modal-overlay"></div>
        <div class="inksoft-modal-content">
          <button class="inksoft-modal-close inksoft-btn" type="button">Close</button>
          <div class="inksoft-loader">Failed to load quick preview.</div>
        </div>
      `);
    }
  }

  return {
    init: init,
    open: open,
    close: close,
    bindClose: bindClose,
    loading: loading,
    openProductQuickView: openProductQuickView
  };
})();